"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  BarChartBig, Music2, Play, Pause, TrendingUp, TrendingDown,
  Headphones, Users, Newspaper, Calendar, Layers, Sparkles, Disc,
  Download, Eye, MessageCircle,
} from "lucide-react";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { cn } from "@/lib/utils";
import { useMediaPlayer, type MediaTrack } from "@/contexts/media-player-context";
import { trackMediaEvent } from "@/services/stats";
import { useScrollDrag } from "@/hooks/use-scroll-drag";

// ─── Types ──────────────────────────────────────────────────────────────────────

type ChartTab = "tracks" | "artists" | "releases" | "news" | "events" | "sync" | "coach";
type SortMode = "most" | "least";

interface ChartEntry {
  id: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  audioUrl?: string;
  genre?: string;
  linkPath?: string;
  count: number;
  tabId: ChartTab;
}

// ─── Tab configuration ────────────────────────────────────────────────────────

interface TabConfig {
  id: ChartTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  entityTypes: string[];
  countLabel: string;
  countIcon: React.ComponentType<{ className?: string }>;
  detailCollection?: string;
  linkBase?: string;
  linkIdField?: string;
  /** Which mediaEvent `type` to count. Tracks count real 'play' events;
   *  everything else counts 'view' events. Undefined = count all events. */
  countType?: string;
}

const TABS: TabConfig[] = [
  {
    id: "tracks",
    label: "Tracks",
    icon: Music2,
    entityTypes: ["track"],
    countLabel: "plays",
    countIcon: Headphones,
    detailCollection: "works",
    linkBase: "/music",
    countType: "play",
  },
  {
    id: "artists",
    label: "Artists",
    icon: Users,
    entityTypes: ["artist"],
    countLabel: "views",
    countIcon: Eye,
    detailCollection: "artists",
    linkBase: "/artists",
    linkIdField: "slug",
  },
  {
    id: "releases",
    label: "Releases",
    icon: Disc,
    entityTypes: ["release"],
    countLabel: "streams",
    countIcon: Headphones,
    detailCollection: "releases",
    linkBase: "/music",
  },
  {
    id: "news",
    label: "News",
    icon: Newspaper,
    entityTypes: ["news", "article", "newsArticle"],
    countLabel: "views",
    countIcon: Eye,
    detailCollection: "news",
    linkBase: "/news",
    linkIdField: "slug",
  },
  {
    id: "events",
    label: "Events",
    icon: Calendar,
    entityTypes: ["event"],
    countLabel: "views",
    countIcon: Eye,
    detailCollection: "events",
    linkBase: "/events",
  },
  {
    id: "sync",
    label: "Sync",
    icon: Layers,
    entityTypes: ["sync", "syncOffer", "syncTrack", "synchronization"],
    countLabel: "views",
    countIcon: Eye,
    detailCollection: "synchronization",
    linkBase: "/synchronization",
  },
  {
    id: "coach",
    label: "AI Coach",
    icon: Sparkles,
    entityTypes: ["creatorAdvisor", "tutorial", "coachQuestion"],
    countLabel: "questions",
    countIcon: MessageCircle,
  },
];

// ─── Load tab data from Firestore ────────────────────────────────────────────

async function loadTabData(tab: TabConfig): Promise<ChartEntry[]> {
  const LIMIT = 1000;

  // Build event query (no orderBy to avoid composite-index requirements)
  const eventQuery =
    tab.entityTypes.length === 1
      ? query(collection(db, "mediaEvents"), where("entityType", "==", tab.entityTypes[0]), limit(LIMIT))
      : query(collection(db, "mediaEvents"), where("entityType", "in", tab.entityTypes), limit(LIMIT));

  const eventsSnap = await getDocs(eventQuery);

  if (eventsSnap.empty) return [];

  // Count only the RELEVANT event type: tracks → real 'play' events, other tabs
  // → 'view' events. Counting every event type made the ranking reflect page
  // views rather than plays (Gordon: "not counting the plays / order doesn't work").
  const countType = tab.countType ?? (tab.detailCollection ? "view" : undefined);

  // Group by entityId, accumulate counts and denorm metadata
  const countMap = new Map<string, { count: number; title: string; subtitle: string; genre: string }>();
  eventsSnap.docs.forEach((d) => {
    const ev = d.data();
    if (!ev.entityId) return;
    if (countType && ev.type !== countType) return;
    const existing = countMap.get(ev.entityId);
    if (existing) {
      existing.count++;
    } else {
      countMap.set(ev.entityId, {
        count: 1,
        title: ev.entityTitle || "",
        subtitle: ev.entityArtist || ev.entityType || "",
        genre: ev.entityGenre || "",
      });
    }
  });

  if (countMap.size === 0) return [];

  // Sort descending and take top 100
  const sorted = Array.from(countMap.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 100);

  if (!tab.detailCollection) {
    // No Firestore detail collection (e.g. coach questions)
    return sorted.map(([id, ev]) => ({
      id,
      title: ev.title || id,
      subtitle: ev.subtitle,
      imageUrl: "",
      genre: ev.genre,
      count: ev.count,
      tabId: tab.id,
    }));
  }

  // Batch-fetch entity details from Firestore.
  // For tracks, also fetch releases to build a cover-art fallback map.
  const topIds = sorted.map(([id]) => id);
  const BATCH = 30;
  const docs: Record<string, Record<string, unknown>> = {};

  // Parallel: entity docs + (for tracks tab) releases for cover fallback
  const releaseCoverMap = new Map<string, string>();

  await Promise.all([
    // Entity detail docs (batched)
    ...Array.from({ length: Math.ceil(topIds.length / BATCH) }, (_, i) =>
      topIds.slice(i * BATCH, i * BATCH + BATCH)
    ).map(async (batch) => {
      try {
        const snap = await getDocs(
          query(collection(db, tab.detailCollection!), where("__name__", "in", batch))
        );
        snap.docs.forEach((d) => { docs[d.id] = { id: d.id, ...d.data() }; });
      } catch { /* non-fatal */ }
    }),
    // Release covers (only needed for the tracks tab)
    tab.id === "tracks"
      ? getDocs(collection(db, "releases")).then((relSnap) => {
          relSnap.docs.forEach((rd) => {
            const rd2 = rd.data();
            const cover = (rd2.coverArtUrl as string) || (rd2.imageUrl as string) || "";
            if (!cover) return;
            if (Array.isArray(rd2.tracks)) {
              (rd2.tracks as any[]).forEach((t) => {
                const wid = t.workId || t.id;
                if (wid && !releaseCoverMap.has(wid)) releaseCoverMap.set(wid, cover);
              });
            }
          });
        }).catch(() => {})
      : Promise.resolve(),
  ]);

  return sorted.map(([id, ev]) => {
    const doc = docs[id] as Record<string, unknown> | undefined;
    const linkId = tab.linkIdField ? ((doc?.[tab.linkIdField] as string) || id) : id;
    const imageUrl =
      (doc?.coverArtUrl as string) ||
      (doc?.imageUrl as string) ||
      releaseCoverMap.get(id) ||          // release cover fallback for tracks
      (doc?.thumbnailUrl as string) || "";
    return {
      id,
      title:
        (doc?.title as string) ||
        (doc?.name as string) ||
        ev.title || id,
      subtitle:
        ((doc as Record<string, Record<string, string>> | undefined)?.artist?.name) ||
        (doc?.artistName as string) ||
        (doc?.genre as string) ||
        ev.subtitle || "",
      imageUrl,
      audioUrl:
        (doc?.mp3Url as string) ||
        (doc?.audioUrl as string) || "",
      genre: (doc?.genre as string) || ev.genre || "",
      linkPath: tab.linkBase ? `${tab.linkBase}/${linkId}` : undefined,
      count: ev.count,
      tabId: tab.id,
    };
  });
}

// ─── Row component ───────────────────────────────────────────────────────────

function ChartRow({
  entry,
  rank,
  onPlay,
  isCurrentTrack,
  isCurrentlyPlaying,
  countLabel,
  CountIcon,
}: {
  entry: ChartEntry;
  rank: number;
  onPlay?: (entry: ChartEntry) => void;
  isCurrentTrack: boolean;
  isCurrentlyPlaying: boolean;
  countLabel: string;
  CountIcon: React.ComponentType<{ className?: string }>;
}) {
  const rankColor =
    rank === 1
      ? "text-amber-400"
      : rank === 2
      ? "text-slate-300"
      : rank === 3
      ? "text-amber-600"
      : "text-muted-foreground";

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors group",
        isCurrentTrack && "bg-secondary/30"
      )}
    >
      {/* Rank */}
      <span className={cn("w-8 text-center font-bold text-base tabular-nums flex-shrink-0", rankColor)}>
        {rank}
      </span>

      {/* Thumbnail */}
      <div className="relative w-10 h-10 flex-shrink-0 rounded-md overflow-hidden bg-muted">
        {entry.imageUrl ? (
          <Image src={entry.imageUrl} alt={entry.title} fill className="object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Music2 className="h-4 w-4 text-muted-foreground/50" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        {entry.linkPath ? (
          <Link
            href={entry.linkPath}
            className="text-sm font-medium truncate leading-tight block hover:underline"
          >
            {entry.title}
          </Link>
        ) : (
          <p className="text-sm font-medium truncate leading-tight">{entry.title}</p>
        )}
        {entry.subtitle && (
          <p className="text-xs text-muted-foreground truncate">{entry.subtitle}</p>
        )}
      </div>

      {/* Count */}
      <div className="flex items-center gap-1 flex-shrink-0 text-xs text-muted-foreground tabular-nums hidden sm:flex">
        <CountIcon className="h-3 w-3" />
        <span>
          {entry.count.toLocaleString()} {countLabel}
        </span>
      </div>

      {/* Play button — only for tracks with audio */}
      {entry.audioUrl && onPlay ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
          onClick={() => onPlay(entry)}
          aria-label={isCurrentlyPlaying ? "Pause" : `Play ${entry.title}`}
        >
          {isCurrentlyPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
      ) : (
        /* Spacer to keep layout consistent */
        <div className="w-8 h-8 flex-shrink-0" />
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ChartsPage() {
  const t = useTranslations("charts");
  const { playTrack, currentTrack, isPlaying, togglePlay } = useMediaPlayer();
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const filterScrollRef = useRef<HTMLDivElement>(null);
  useScrollDrag(tabScrollRef);
  useScrollDrag(filterScrollRef);

  const [activeTab, setActiveTab] = usePersistedState<ChartTab>("charts-tab", "tracks");
  const [tabDataCache, setTabDataCache] = useState<Partial<Record<ChartTab, ChartEntry[]>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>("most");
  const [selectedGenre, setSelectedGenre] = useState<string>("all");

  const tabConfig = TABS.find((t) => t.id === activeTab)!;

  // ── Load data when tab changes ───────────────────────────────────────────
  useEffect(() => {
    if (tabDataCache[activeTab] !== undefined) return; // cached
    let cancelled = false;
    setIsLoading(true);
    loadTabData(tabConfig)
      .then((data) => {
        if (!cancelled) setTabDataCache((prev) => ({ ...prev, [activeTab]: data }));
      })
      .catch(() => {
        if (!cancelled) setTabDataCache((prev) => ({ ...prev, [activeTab]: [] }));
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Reset genre filter when switching tabs
  useEffect(() => { setSelectedGenre("all"); }, [activeTab]);

  const rawEntries = tabDataCache[activeTab] ?? [];

  // Genre list (tracks + releases only)
  const genres = useMemo(() => {
    if (activeTab !== "tracks" && activeTab !== "releases") return [];
    const set = new Set<string>();
    rawEntries.forEach((e) => { if (e.genre) set.add(e.genre); });
    return Array.from(set).sort();
  }, [rawEntries, activeTab]);

  // Filtered + sorted entries
  const displayed = useMemo(() => {
    let result = rawEntries;
    if (selectedGenre !== "all") result = result.filter((e) => e.genre === selectedGenre);
    return sortMode === "least"
      ? [...result].sort((a, b) => a.count - b.count)
      : [...result].sort((a, b) => b.count - a.count);
  }, [rawEntries, selectedGenre, sortMode]);

  // ── Play handler (tracks only) ───────────────────────────────────────────
  const handlePlay = useCallback(
    (entry: ChartEntry) => {
      if (!entry.audioUrl) return;
      if (currentTrack?.id === entry.id) { togglePlay(); return; }
      const track: MediaTrack = {
        id: entry.id,
        title: entry.title,
        artist: entry.subtitle,
        imageUrl: entry.imageUrl,
        audioUrl: entry.audioUrl,
        mediaType: "audio",
        genre: entry.genre,
      };
      playTrack(track);
      trackMediaEvent({
        type: "play",
        entityType: "track",
        entityId: entry.id,
        entityTitle: entry.title,
        entityArtist: entry.subtitle,
        entityGenre: entry.genre,
      });
    },
    [playTrack, currentTrack, togglePlay]
  );

  const switchTab = useCallback((id: ChartTab) => {
    setActiveTab(id);
    setSortMode("most");
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Spacer for fixed bar on mobile ── */}
      <div className="h-12 sm:hidden" aria-hidden="true" />

      {/* ── Sticky filter bar ── */}
      <div className="fixed top-[var(--header-height)] left-0 w-full z-30 bg-background border-b border-border/50
                      sm:sticky sm:top-[var(--header-height)] sm:bg-background/90 sm:backdrop-blur-md">
        <div className="w-full px-3 sm:px-4 pt-3 pb-2 space-y-2">

          {/* Title row — always at the top */}
          <div className="flex items-center gap-2">
            <BarChartBig className="h-5 w-5 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-bold tracking-tight leading-tight">{t("title")}</h1>
              <p className="text-[11px] text-muted-foreground leading-tight">
                {t(`sub.${activeTab}`)}
              </p>
            </div>
          </div>

          {/* Tab strip */}
          <div
            ref={tabScrollRef}
            className="flex overflow-x-auto hide-scrollbar drag-scroll gap-1.5 sm:cursor-auto sm:flex-wrap"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className={cn(
                    "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all whitespace-nowrap",
                    activeTab === tab.id
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground/60 hover:text-foreground"
                  )}
                >
                  <Icon className="h-3 w-3 flex-shrink-0" />
                  {t(`tab.${tab.id}`)}
                </button>
              );
            })}
          </div>

          {/* Sort buttons + count */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setSortMode("most")}
              className={cn(
                "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all whitespace-nowrap",
                sortMode === "most"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:border-foreground/60 hover:text-foreground"
              )}
            >
              <TrendingUp className="h-3 w-3 flex-shrink-0" />
              Most
            </button>
            <button
              onClick={() => setSortMode("least")}
              className={cn(
                "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all whitespace-nowrap",
                sortMode === "least"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:border-foreground/60 hover:text-foreground"
              )}
            >
              <TrendingDown className="h-3 w-3 flex-shrink-0" />
              Least
            </button>
            {!isLoading && (
              <span className="ml-auto text-xs text-muted-foreground">
                {displayed.length} {activeTab === "coach" ? "topics" : activeTab}
              </span>
            )}
          </div>

          {/* Genre chips (tracks + releases only) */}
          {genres.length > 0 && (
            <div
              ref={filterScrollRef}
              className="flex overflow-x-auto hide-scrollbar drag-scroll gap-1.5 pb-0.5 sm:cursor-auto sm:flex-wrap"
            >
              {["all", ...genres].map((g) => (
                <button
                  key={g}
                  onClick={() => setSelectedGenre(g)}
                  className={cn(
                    "flex-shrink-0 px-3 py-1.5 rounded-md border text-xs font-medium transition-all whitespace-nowrap capitalize",
                    selectedGenre === g
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground/60 hover:text-foreground"
                  )}
                >
                  {g === "all" ? "All" : g}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Page content ── */}
      <div className="w-full px-3 sm:px-4 py-4">

        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg bg-muted/50 animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && displayed.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            {(() => {
              const Icon = tabConfig.icon;
              return <Icon className="h-16 w-16 text-muted-foreground/20 mb-4" />;
            })()}
            <h2 className="text-lg font-semibold mb-1">{t('emptyTitle')}</h2>
            <p className="text-sm text-muted-foreground max-w-xs">
              {activeTab === "coach" ? t('emptyCoach') : t('emptyBody', { what: t(`tab.${activeTab}`) })}
            </p>
          </div>
        )}

        {/* Chart list */}
        {!isLoading && displayed.length > 0 && (
          <div className="space-y-0.5">
            {displayed.map((entry, i) => (
              <ChartRow
                key={entry.id}
                entry={entry}
                rank={i + 1}
                onPlay={activeTab === "tracks" ? handlePlay : undefined}
                isCurrentTrack={currentTrack?.id === entry.id}
                isCurrentlyPlaying={currentTrack?.id === entry.id && isPlaying}
                countLabel={tabConfig.countLabel}
                CountIcon={tabConfig.countIcon}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
